import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Checkbox,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
  formatHeatmapPositionCoord,
  formatPositionKeyValue,
  imageDensityPositionTableColumns,
  isHeatmapPositionColumn,
} from '../../utils/imageDensityPoints';
import { formatDisplayCellValue } from '../../utils/imageDensityTooltip';

const PANEL_WIDTH = 420;
const PANEL_HEIGHT = 280;
const MIN_WIDTH = 280;
const MIN_HEIGHT = 160;
const HANDLE = 10;
/** Por encima del editor de gráficos (modal) y por debajo de la alerta de cambios sin guardar. */
const PANEL_Z_INDEX = 1350;

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function cellText(row, column, columnDurationFormats) {
  if (!row || !column) return '—';
  if (isHeatmapPositionColumn(column)) {
    return formatHeatmapPositionCoord(row[column]) || '—';
  }
  if (!Object.prototype.hasOwnProperty.call(row, column)) return '—';
  const value = row[column];
  if (value != null && typeof value === 'object') {
    const formatted = formatPositionKeyValue(value);
    return formatted || '—';
  }
  return formatDisplayCellValue(value, column, columnDurationFormats);
}

const RESIZE_HANDLES = [
  { edge: 'n', cursor: 'ns-resize', sx: { top: 0, left: HANDLE, right: HANDLE, height: HANDLE } },
  { edge: 's', cursor: 'ns-resize', sx: { bottom: 0, left: HANDLE, right: HANDLE, height: HANDLE } },
  { edge: 'e', cursor: 'ew-resize', sx: { right: 0, top: HANDLE, bottom: HANDLE, width: HANDLE } },
  { edge: 'w', cursor: 'ew-resize', sx: { left: 0, top: HANDLE, bottom: HANDLE, width: HANDLE } },
  { edge: 'ne', cursor: 'nesw-resize', sx: { top: 0, right: 0, width: HANDLE + 4, height: HANDLE + 4 } },
  { edge: 'nw', cursor: 'nwse-resize', sx: { top: 0, left: 0, width: HANDLE + 4, height: HANDLE + 4 } },
  { edge: 'se', cursor: 'nwse-resize', sx: { bottom: 0, right: 0, width: HANDLE + 6, height: HANDLE + 6 } },
  { edge: 'sw', cursor: 'nesw-resize', sx: { bottom: 0, left: 0, width: HANDLE + 4, height: HANDLE + 4 } },
];

/**
 * Tabla flotante, arrastrable y redimensionable con las filas a posicionar.
 */
export default function ImageDensityPositionTable({
  open,
  anchorRef,
  entities = [],
  selectedKey = '',
  onSelectKey,
  positionKeyColumn,
  tooltipColumns,
  metricColumns,
  columnDurationFormats,
}) {
  const panelRef = useRef(null);
  const interactionRef = useRef(null);
  const [pos, setPos] = useState({ left: 24, top: 96 });
  const [size, setSize] = useState({ width: PANEL_WIDTH, height: PANEL_HEIGHT });
  const [hiddenCols, setHiddenCols] = useState(() => new Set());
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const columnsInitialized = useRef(false);

  const columns = useMemo(
    () =>
      imageDensityPositionTableColumns(entities, {
        positionKeyColumn,
        tooltipColumns,
        metricColumns,
      }),
    [entities, positionKeyColumn, tooltipColumns, metricColumns]
  );

  const unplacedCount = entities.filter((e) => !e.placed).length;
  const orderedEntities = useMemo(
    () =>
      [...entities].sort((a, b) => (Number(a.markerNumber) || 0) - (Number(b.markerNumber) || 0)),
    [entities]
  );

  const visibleColumns = useMemo(
    () => columns.filter((col) => !hiddenCols.has(col)),
    [columns, hiddenCols]
  );

  useEffect(() => {
    if (!open) {
      columnsInitialized.current = false;
      return;
    }
    if (columnsInitialized.current || columns.length === 0) return;
    columnsInitialized.current = true;
    setHiddenCols(new Set(columns));
  }, [open, columns]);

  const toggleColumn = useCallback((col) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef?.current;
    const width = size.width;
    const height = size.height;
    let left = 24;
    let top = 96;
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      left = r.right + 12;
      if (left + width > window.innerWidth - 12) {
        left = clamp(r.right - width - 8, 8, window.innerWidth - width - 8);
      }
      top = clamp(r.top, 8, Math.max(8, window.innerHeight - height - 8));
    }
    setPos({ left, top });
    // Solo al abrir: no resetear si el usuario ya redimensionó.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    const onMove = (event) => {
      const interaction = interactionRef.current;
      if (!interaction) return;
      if (interaction.type === 'drag') {
        setPos({
          left: clamp(event.clientX - interaction.offsetX, 8, window.innerWidth - interaction.startW - 8),
          top: clamp(event.clientY - interaction.offsetY, 8, window.innerHeight - interaction.startH - 8),
        });
        return;
      }
      const { edge, startX, startY, startLeft, startTop, startW, startH } = interaction;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      let left = startLeft;
      let top = startTop;
      let width = startW;
      let height = startH;
      if (edge.includes('e')) {
        width = clamp(startW + dx, MIN_WIDTH, window.innerWidth - startLeft - 8);
      }
      if (edge.includes('s')) {
        height = clamp(startH + dy, MIN_HEIGHT, window.innerHeight - startTop - 8);
      }
      if (edge.includes('w')) {
        width = clamp(startW - dx, MIN_WIDTH, startLeft + startW - 8);
        left = startLeft + startW - width;
      }
      if (edge.includes('n')) {
        height = clamp(startH - dy, MIN_HEIGHT, startTop + startH - 8);
        top = startTop + startH - height;
      }
      setPos({ left, top });
      setSize({ width, height });
    };
    const onUp = () => {
      interactionRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [open]);

  const onHeaderPointerDown = useCallback((event) => {
    if (event.button !== 0) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    interactionRef.current = {
      type: 'drag',
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      startW: rect.width,
      startH: rect.height,
    };
    event.preventDefault();
  }, []);

  const onResizePointerDown = useCallback((edge) => (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    interactionRef.current = {
      type: 'resize',
      edge,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: rect.left,
      startTop: rect.top,
      startW: rect.width,
      startH: rect.height,
    };
  }, []);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <Paper
      ref={panelRef}
      elevation={8}
      sx={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        width: size.width,
        height: size.height,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        zIndex: PANEL_Z_INDEX,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        onPointerDown={onHeaderPointerDown}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1,
          py: 0.75,
          cursor: 'grab',
          bgcolor: 'grey.100',
          borderBottom: 1,
          borderColor: 'divider',
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        <DragIndicatorIcon fontSize="small" color="action" />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" noWrap>
            Posicionar marcadores
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {unplacedCount === 0
              ? 'Todos fijados. Arrastra en la imagen o elige una fila para moverla.'
              : `Elige una fila y haz clic en la imagen · ${unplacedCount} sin fijar`}
          </Typography>
        </Box>
        <Tooltip title="Mostrar columnas de la consulta">
          <IconButton
            size="small"
            aria-label="Columnas de la consulta"
            onClick={(event) => setColumnsMenuAnchor(event.currentTarget)}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={columnsMenuAnchor}
          open={Boolean(columnsMenuAnchor)}
          onClose={() => setColumnsMenuAnchor(null)}
          sx={{ zIndex: PANEL_Z_INDEX + 2 }}
        >
          {columns.map((col) => (
            <MenuItem key={col} dense onClick={() => toggleColumn(col)}>
              <Checkbox size="small" checked={!hiddenCols.has(col)} sx={{ p: 0.5, mr: 0.5 }} />
              <ListItemText primary={col.replace(/_/g, ' ')} />
            </MenuItem>
          ))}
        </Menu>
      </Box>
      <TableContainer sx={{ flex: 1, minHeight: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 36, py: 0.5, fontWeight: 700, whiteSpace: 'nowrap' }}>Nº</TableCell>
              <TableCell sx={{ py: 0.5, fontWeight: 700, whiteSpace: 'nowrap' }}>Elemento</TableCell>
              <TableCell sx={{ width: 88, py: 0.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
                Estado
              </TableCell>
              {visibleColumns.map((col) => {
                const positionCol = isHeatmapPositionColumn(col);
                return (
                  <TableCell
                    key={col}
                    sx={{
                      py: 0.5,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      bgcolor: positionCol ? 'warning.50' : undefined,
                    }}
                  >
                    {col.replace(/_/g, ' ')}
                    {positionCol && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="warning.dark"
                        sx={{ ml: 0.5, fontWeight: 600 }}
                      >
                        temporal
                      </Typography>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {entities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 3}>
                  <Typography variant="caption" color="text.secondary">
                    {positionKeyColumn
                      ? `No hay valores en «${positionKeyColumn}». Revisa que la vista previa tenga filas.`
                      : 'Elige una columna identificador para ver los elementos.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              orderedEntities.map((entity) => {
                const selected = entity.key === selectedKey;
                return (
                  <TableRow
                    key={entity.key}
                    hover
                    selected={selected}
                    onClick={() => onSelectKey?.(selected ? '' : entity.key)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: selected ? 'action.selected' : undefined,
                    }}
                  >
                    <TableCell
                      sx={{ py: 0.5, fontWeight: 700, whiteSpace: 'nowrap', color: 'primary.main' }}
                    >
                      {entity.markerNumber}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 0.5,
                        maxWidth: 240,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: selected ? 700 : 500,
                      }}
                      title={entity.label || entity.key}
                    >
                      {entity.label || entity.key}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, whiteSpace: 'nowrap' }}>
                      {entity.placed ? 'Fijado' : 'Pendiente'}
                    </TableCell>
                    {visibleColumns.map((col) => {
                      const positionCol = isHeatmapPositionColumn(col);
                      return (
                        <TableCell
                          key={`${entity.key}-${col}`}
                          sx={{
                            py: 0.5,
                            maxWidth: 140,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            bgcolor: positionCol ? 'warning.50' : undefined,
                          }}
                        >
                          {cellText(entity.row, col, columnDurationFormats)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {RESIZE_HANDLES.map((h) => (
        <Box
          key={h.edge}
          onPointerDown={onResizePointerDown(h.edge)}
          sx={{
            position: 'absolute',
            zIndex: 2,
            cursor: h.cursor,
            ...h.sx,
          }}
        />
      ))}
    </Paper>,
    document.body
  );
}
